import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import backwardArrow from "../../../../assets/icons/backward-arrow.svg";
import { useContentfulContext } from "../../../../contexts/ContentfulContext";
import { Search } from "lucide-react";
import { AgTableCard, Input } from "../../../../components/UI";
import {
    getTealiumDataLayerReportByTaskId,
    updateTealiumDataLayer,
    validateTealiumDataLayer,
    runTealiumDataLayerCrawler,
    getTealiumDataLayerValidationChanges,
    getSavedTaskLogs,
    getCrawlHistory,
} from "../../../../utils/APIs";
import PathCrawlsTab from "./PathCrawlsTab";
import { useUserContext } from "../../../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import PrimaryModal from "../../../../components/UI/Modal/PrimaryModal";
import { formatDate } from "../../../../utils/converter";
import { toast } from "react-toastify";

const CRAWLER_POLLING_INTERVAL = 15000;

// ─── Schema helpers ───────────────────────────────────────────────────────────

const getLatestCapture = (record) => {
    const captures = record?.data_captures;
    if (!captures?.length) return null;
    return captures.reduce((a, b) =>
        new Date(a.date_captured) >= new Date(b.date_captured) ? a : b
    );
};

const getLatestCaptureIdx = (record) => {
    const captures = record?.data_captures;
    if (!captures?.length) return 0;
    return captures.reduce((bestIdx, capture, idx, arr) =>
        new Date(capture.date_captured) >= new Date(arr[bestIdx].date_captured) ? idx : bestIdx
    , 0);
};

// ─── Validation Status Badge ──────────────────────────────────────────────────

const ValidationStatusBadge = ({ status }) => {
    if (status === "passed") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Validated
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                Failed
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
            Unvalidated
        </span>
    );
};

// ─── Changes Badge ────────────────────────────────────────────────────────────
// Shown in the "Changes" column. Green = no rule-scoped changes, amber = changes detected.

const ChangesBadge = ({ hasChanges }) => {
    if (hasChanges === null || hasChanges === undefined) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                No data
            </span>
        );
    }
    if (hasChanges) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Changes detected
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            No changes
        </span>
    );
};

// ─── Changes Diff Modal ───────────────────────────────────────────────────────
// Tabbed modal: Tab 1 = rule-scoped changes, Tab 2 = all attribute changes.

const ChangesDiffModal = ({ fullUrl, changeData, onClose }) => {
    const [activeTab, setActiveTab] = useState("rule_scoped");

    const ruleScopedChanges   = changeData?.rule_scoped_changes   ?? {};
    const allAttributeChanges = changeData?.all_attribute_changes ?? {};

    const ruleEntries = Object.entries(ruleScopedChanges);
    const allEntries  = Object.entries(allAttributeChanges);

    const captureDates      = changeData?.capture_dates;
    const validationSummary = changeData?.validation_summary;

    const tabs = [
        { key: "rule_scoped", label: `Rule-Scoped Changes${ruleEntries.length ? ` (${ruleEntries.length})` : ""}` },
        { key: "all",         label: `All Changes${allEntries.length ? ` (${allEntries.length})` : ""}`           },
    ];

    const renderDiffTable = (entries) => {
        if (!entries.length) {
            return (
                <p className="text-sm text-gray-400 italic text-center py-8">No changes detected between the two most recent captures.</p>
            );
        }
        return (
            <table className="w-full text-sm text-left text-gray-700 border border-gray-200" style={{ tableLayout: "fixed" }}>
                <thead className="text-xs text-gray-600 uppercase bg-gray-100">
                    <tr>
                        <th className="px-4 py-3 font-semibold" style={{ width: "30%" }}>Attribute</th>
                        <th className="px-4 py-3 font-semibold" style={{ width: "35%" }}>Previous</th>
                        <th className="px-4 py-3 font-semibold" style={{ width: "35%" }}>Current</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map(([path, { from, to }], idx) => (
                        <tr key={path} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="px-4 py-3 font-mono text-xs font-medium text-gray-900" style={{ wordBreak: "break-all" }}>
                                {path}
                            </td>
                            <td className="px-4 py-3 text-xs text-red-700 bg-red-50" style={{ wordBreak: "break-all" }}>
                                {from === null ? <span className="italic text-gray-400">—</span> : (
                                    <code className="font-mono">{typeof from === "object" ? JSON.stringify(from) : String(from)}</code>
                                )}
                            </td>
                            <td className="px-4 py-3 text-xs text-green-700 bg-green-50" style={{ wordBreak: "break-all" }}>
                                {to === null ? <span className="italic text-gray-400">—</span> : (
                                    <code className="font-mono">{typeof to === "object" ? JSON.stringify(to) : String(to)}</code>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <PrimaryModal title="Capture Changes" hideModal={onClose} widthClass="max-w-3xl">
            <div className="flex flex-col" style={{ maxHeight: "80vh" }}>

                {/* Header meta strip */}
                <div className="px-5 py-3 border-b bg-gray-50 text-xs text-gray-500 space-y-1">
                    <p className="font-medium text-gray-700 truncate">{fullUrl}</p>
                    {captureDates && (
                        <p>
                            Comparing&nbsp;
                            <span className="font-medium text-gray-700">{formatDate(captureDates.previous)}</span>
                            &nbsp;→&nbsp;
                            <span className="font-medium text-gray-700">{formatDate(captureDates.latest)}</span>
                        </p>
                    )}
                    {validationSummary && (
                        <div className="flex gap-5 pt-0.5">
                            {validationSummary.previous && (
                                <span>
                                    Previous validation:&nbsp;
                                    <span className={validationSummary.previous.validation_status ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                        {validationSummary.previous.validation_status ? "Passed" : "Failed"}
                                    </span>
                                    &nbsp;({validationSummary.previous.passed_count}✓ / {validationSummary.previous.failed_count}✗)
                                </span>
                            )}
                            {validationSummary.latest && (
                                <span>
                                    Latest validation:&nbsp;
                                    <span className={validationSummary.latest.validation_status ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                        {validationSummary.latest.validation_status ? "Passed" : "Failed"}
                                    </span>
                                    &nbsp;({validationSummary.latest.passed_count}✓ / {validationSummary.latest.failed_count}✗)
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex border-b">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                                activeTab === tab.key
                                    ? "border-b-2 border-primary text-primary"
                                    : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="overflow-y-auto p-5">
                    {activeTab === "rule_scoped" && renderDiffTable(ruleEntries)}
                    {activeTab === "all"         && renderDiffTable(allEntries)}
                </div>
            </div>
        </PrimaryModal>
    );
};

// ─── Per-Row Validation Report Modal ─────────────────────────────────────────

const RowValidationModal = ({ domain, result, onClose }) => {
    const validation = result?.validation;
    const hasError   = !!result?.error;
    const isValid    = validation?.is_valid;
    const violations = validation?.violations ?? [];
    const passed     = validation?.passed ?? [];

    return (
        <PrimaryModal title="Validation Report" hideModal={onClose} widthClass="max-w-2xl">
            <div className="flex flex-col" style={{ maxHeight: "75vh" }}>
                {/* Header strip */}
                <div className={`flex items-center justify-between px-5 py-3 border-b text-sm font-medium ${
                    hasError
                        ? "bg-orange-50 border-orange-200 text-orange-800"
                        : isValid
                            ? "bg-green-50 border-green-200 text-green-800"
                            : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    <span className="flex items-center gap-2 flex-wrap">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${hasError ? "bg-orange-400" : isValid ? "bg-green-500" : "bg-red-500"}`} />
                        {hasError
                            ? "Validation error"
                            : isValid
                                ? "All rules passed"
                                : `${validation?.violation_count ?? 0} violation${validation?.violation_count !== 1 ? "s" : ""} found`}
                        {result?.fromDb && validation?.date_validated && (
                            <span className="text-xs font-normal opacity-60 italic">
                                · stored result from {new Date(validation.date_validated).toLocaleString()}
                            </span>
                        )}
                    </span>
                    <span className="text-xs font-normal opacity-70 truncate max-w-xs">{domain}</span>
                </div>

                <div className="overflow-y-auto p-5 space-y-5">
                    {/* Stats row */}
                    {!hasError && (
                        <div className="flex gap-6 text-xs text-gray-500">
                            <span>{validation?.total_rules_evaluated ?? 0} rules evaluated</span>
                            <span className="text-green-600 font-medium">{validation?.passed_count ?? 0} passed</span>
                            <span className={`font-medium ${validation?.violation_count ? "text-red-500" : "text-gray-400"}`}>
                                {validation?.violation_count ?? 0} failed
                            </span>
                        </div>
                    )}

                    {hasError && (
                        <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700">
                            {result.error}
                        </div>
                    )}

                    {violations.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold uppercase text-red-600 tracking-wide mb-2">Failed Rules</p>
                            <div className="space-y-2">
                                {violations.map((v, i) => (
                                    <div key={i} className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <code className="text-xs font-mono font-semibold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                                    {v.variable_name}
                                                </code>
                                                <span className="text-xs text-gray-500 capitalize">
                                                    {v.rule_type?.replace(/_/g, " ")}
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-400 capitalize flex-shrink-0">{v.scope}</span>
                                        </div>
                                        <p className="text-xs text-red-700 font-medium">{v.reason}</p>
                                        {v.actual_value !== undefined && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Actual value: <code className="font-mono bg-white px-1 rounded">{String(v.actual_value)}</code>
                                            </p>
                                        )}
                                        {v.allowed_values?.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Allowed: <code className="font-mono">{v.allowed_values.join(", ")}</code>
                                            </p>
                                        )}
                                        {v.regex_pattern && (
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Pattern: <code className="font-mono">{v.regex_pattern}</code>
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {passed.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold uppercase text-green-600 tracking-wide mb-2">Passed Rules</p>
                            <div className="space-y-1.5">
                                {passed.map((p, i) => (
                                    <div key={i} className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                        <code className="text-xs font-mono text-green-800">{p.variable_name}</code>
                                        <span className="text-xs text-gray-400 capitalize ml-auto">{p.rule_type?.replace(/_/g, " ")}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!hasError && violations.length === 0 && passed.length === 0 && (
                        <p className="text-sm text-gray-400 italic text-center py-6">No rules were evaluated for this page.</p>
                    )}
                </div>
            </div>
        </PrimaryModal>
    );
};

// ─── Bulk Validation Results Modal ───────────────────────────────────────────

const ValidationResultsModal = ({ results, onClose }) => {
    const [activeUrl, setActiveUrl] = useState(results?.[0]?.domain ?? null);

    const activeResult    = results.find((r) => r.domain === activeUrl);
    const overallValid    = results.every((r) => r.validation?.is_valid);
    const totalViolations = results.reduce((sum, r) => sum + (r.validation?.violation_count ?? 0), 0);
    const totalRules      = results.reduce((sum, r) => sum + (r.validation?.total_rules_evaluated ?? 0), 0);

    return (
        <PrimaryModal title="Validation Results" hideModal={onClose} widthClass="max-w-5xl">
            <div className="flex flex-col" style={{ height: "75vh" }}>
                <div className={`flex items-center gap-6 px-5 py-3 text-sm font-medium border-b ${
                    overallValid ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    <span className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${overallValid ? "bg-green-500" : "bg-red-500"}`} />
                        {overallValid ? "All pages passed" : `${totalViolations} violation${totalViolations !== 1 ? "s" : ""} found`}
                    </span>
                    <span className="text-gray-500 font-normal">{results.length} pages · {totalRules} rules evaluated</span>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    <div className="w-64 flex-shrink-0 border-r border-gray-200 overflow-y-auto">
                        {results.map((r) => {
                            const isValid    = r.validation?.is_valid;
                            const violations = r.validation?.violation_count ?? 0;
                            const isActive   = r.domain === activeUrl;
                            return (
                                <button key={r.domain} onClick={() => setActiveUrl(r.domain)}
                                    className={`w-full text-left px-4 py-3 border-b border-gray-100 flex items-start gap-2 transition-colors ${
                                        isActive ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-gray-50"
                                    }`}>
                                    <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${isValid ? "bg-green-500" : "bg-red-500"}`} />
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium text-gray-800 truncate">{r.domain.replace(/^https?:\/\//, "")}</p>
                                        {!isValid
                                            ? <p className="text-xs text-red-500 mt-0.5">{violations} violation{violations !== 1 ? "s" : ""}</p>
                                            : <p className="text-xs text-green-600 mt-0.5">Passed</p>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex-1 overflow-y-auto p-5">
                        {activeResult ? (
                            <>
                                <p className="text-sm font-semibold text-gray-700 mb-1 truncate">{activeResult.domain}</p>
                                <div className="flex gap-4 text-xs text-gray-500 mb-4">
                                    <span>{activeResult.validation?.total_rules_evaluated ?? 0} rules evaluated</span>
                                    <span className="text-green-600">{activeResult.validation?.passed_count ?? 0} passed</span>
                                    <span className={activeResult.validation?.violation_count ? "text-red-500" : "text-gray-400"}>
                                        {activeResult.validation?.violation_count ?? 0} violations
                                    </span>
                                </div>

                                {activeResult.validation?.violations?.length > 0 && (
                                    <div className="mb-5">
                                        <p className="text-xs font-semibold uppercase text-red-600 tracking-wide mb-2">Violations</p>
                                        <div className="space-y-2">
                                            {activeResult.validation.violations.map((v, i) => (
                                                <div key={i} className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <code className="text-xs font-mono font-semibold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">{v.variable_name}</code>
                                                        <span className="text-xs text-gray-500 capitalize">{v.rule_type?.replace(/_/g, " ")}</span>
                                                        <span className="ml-auto text-xs text-gray-400 capitalize">{v.scope}</span>
                                                    </div>
                                                    <p className="text-xs text-red-700">{v.reason}</p>
                                                    {v.actual_value !== undefined && <p className="text-xs text-gray-500 mt-1">Actual: <code className="font-mono">{String(v.actual_value)}</code></p>}
                                                    {v.allowed_values && <p className="text-xs text-gray-500 mt-0.5">Allowed: <code className="font-mono">{v.allowed_values.join(", ")}</code></p>}
                                                    {v.regex_pattern && <p className="text-xs text-gray-500 mt-0.5">Pattern: <code className="font-mono">{v.regex_pattern}</code></p>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeResult.validation?.passed?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold uppercase text-green-600 tracking-wide mb-2">Passed</p>
                                        <div className="space-y-1.5">
                                            {activeResult.validation.passed.map((p, i) => (
                                                <div key={i} className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                                    <code className="text-xs font-mono text-green-800">{p.variable_name}</code>
                                                    <span className="text-xs text-gray-400 capitalize ml-auto">{p.rule_type?.replace(/_/g, " ")}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeResult.validation?.is_valid && activeResult.validation?.passed?.length === 0 && (
                                    <p className="text-sm text-gray-400 italic text-center py-8">No rules were evaluated for this page.</p>
                                )}
                                {activeResult.error && (
                                    <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700">
                                        Validation failed: {activeResult.error}
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-gray-400 italic text-center py-8">Select a page to view its results.</p>
                        )}
                    </div>
                </div>
            </div>
        </PrimaryModal>
    );
};

// ─── Crawl History / View Changes Modal ──────────────────────────────────────
// Lets the user pick two dates from the task's crawl_history and then fetches
// the diff between those two captures.

const CrawlHistoryModal = ({ taskId, token, organizationId, onSubmit, onClose }) => {
    const [history,   setHistory]   = useState([]);
    const [loadingH,  setLoadingH]  = useState(true);
    const [dateA,     setDateA]     = useState(""); // "from" (older)
    const [dateB,     setDateB]     = useState(""); // "to"   (newer)
    const [error,     setError]     = useState("");

    useEffect(() => {
        const load = async () => {
            try {
                const result = await getCrawlHistory(token, organizationId, taskId);
                setHistory(Array.isArray(result?.data) ? result.data : []);
            } catch {
                setError("Failed to load crawl history.");
            } finally {
                setLoadingH(false);
            }
        };
        load();
    }, [taskId, token, organizationId]);

    const formatLabel = (iso) => {
        try {
            return new Date(iso).toLocaleString(undefined, {
                year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit"
            });
        } catch {
            return iso;
        }
    };

    const handleSubmit = () => {
        if (!dateA || !dateB) {
            setError("Please select both dates.");
            return;
        }
        if (dateA === dateB) {
            setError("Please select two different dates.");
            return;
        }
        // Pass older → newer regardless of selection order
        const [from, to] = new Date(dateA) <= new Date(dateB)
            ? [dateA, dateB]
            : [dateB, dateA];
        onSubmit(from, to);
    };

    return (
        <PrimaryModal title="View Changes Between Crawls" hideModal={onClose} widthClass="max-w-md">
            <div className="p-5 space-y-5">
                {loadingH ? (
                    <p className="text-sm text-gray-500 text-center py-4">Loading crawl history…</p>
                ) : history.length < 2 ? (
                    <p className="text-sm text-gray-500 italic text-center py-4">
                        At least two crawl runs are needed to compare changes.
                    </p>
                ) : (
                    <>
                        <p className="text-sm text-gray-600">
                            Select two crawl dates to compare the data layer snapshots captured on those runs.
                        </p>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    From (older crawl)
                                </label>
                                <select
                                    value={dateA}
                                    onChange={(e) => { setDateA(e.target.value); setError(""); }}
                                    className="w-full px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary"
                                >
                                    <option value="">— Select a crawl date —</option>
                                    {history.map((iso) => (
                                        <option key={iso} value={iso}>{formatLabel(iso)}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    To (newer crawl)
                                </label>
                                <select
                                    value={dateB}
                                    onChange={(e) => { setDateB(e.target.value); setError(""); }}
                                    className="w-full px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary"
                                >
                                    <option value="">— Select a crawl date —</option>
                                    {history.map((iso) => (
                                        <option key={iso} value={iso}>{formatLabel(iso)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {error && <p className="text-xs text-red-500">{error}</p>}

                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
                            >
                                Compare
                            </button>
                        </div>
                    </>
                )}
            </div>
        </PrimaryModal>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TealiumDataLayerReport = () => {
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState(false);
    const [data,       setData]       = useState([]);
    const [searchText, setSearchText] = useState("");
    const [gridApi,    setGridApi]    = useState(null);

    const { content }  = useContentfulContext();
    const { userData } = useUserContext();
    const navigate     = useNavigate();
    const taskId       = window.location.pathname.split("/").filter(Boolean).pop();

    // Page-level tab
    const [pageTab, setPageTab] = useState("site_crawl");

    // Modal states
    const [imageModal,   setImageModal]   = useState({ open: false, url: "", domain: "" });
    const [detailsModal, setDetailsModal] = useState({ open: false, rowData: null });
    const [activeTab,    setActiveTab]    = useState("tealium_integration");

    // Custom params (modal editing)
    const [editedCustomParams, setEditedCustomParams] = useState([]);
    const [saving,             setSaving]             = useState(false);

    // Inline grid edits
    const [modifiedRows, setModifiedRows] = useState({});

    // Per-row validation results keyed by full_url
    const [validationMap,     setValidationMap]     = useState({});
    const [rowReportModal,    setRowReportModal]    = useState(null);
    const [validationResults, setValidationResults] = useState(null);
    const [validating,        setValidating]        = useState(false);
    const [crawling,          setCrawling]          = useState(false);

    // Per-row validation-change diffs keyed by `${full_url}__${event_type}`
    const [changesMap,        setChangesMap]        = useState({});
    const [changesDiffModal,  setChangesDiffModal]  = useState(null); // { fullUrl, changeData }
    const [crawlHistoryModal, setCrawlHistoryModal] = useState(false);
    const [loadingChanges,    setLoadingChanges]    = useState(false);

    // Ref to hold the polling interval so it can be cleared from any context
    const crawlPollRef = useRef(null);

    const hasUnsavedChanges = Object.keys(modifiedRows).length > 0;

    // ── Crawler Polling ───────────────────────────────────────────────────────
    // Polls getSavedTaskLogs every 5 seconds. A crawler run is considered "in
    // progress" when the most recent "info" (Task Started) log has no subsequent
    // "success" or "error" log after it. Once one appears, polling stops.

    const stopPolling = () => {
        if (crawlPollRef.current) {
            clearInterval(crawlPollRef.current);
            crawlPollRef.current = null;
        }
    };

    const startCrawlerPolling = () => {
        stopPolling();
        crawlPollRef.current = setInterval(async () => {
            try {
                const logs    = await getSavedTaskLogs(userData.token, userData.selectedOrganization.org_token, taskId);
                const logList = Array.isArray(logs?.data) ? logs.data : [];

                const sorted       = [...logList].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                const lastStartIdx = sorted.map((l) => l.type).lastIndexOf("info");

                // No "Task Started" log yet — keep waiting
                if (lastStartIdx === -1) return;

                const logsAfterStart = sorted.slice(lastStartIdx + 1);
                const finished       = logsAfterStart.some((l) => l.type === "success" || l.type === "error");

                if (finished) {
                    stopPolling();
                    setCrawling(false);
                    await getTealiumDataLayerReportData();
                    const errorLog = logsAfterStart.find((l) => l.type === "error");
                    if (errorLog) {
                        toast.error(`Crawler failed: ${errorLog.description}`);
                    } else {
                        toast.success("Crawler complete — data has been refreshed.");
                    }
                }
            } catch {
                // Non-fatal — keep polling; if it continues to fail, the
                // user can manually refresh the page.
                stopPolling();
                setCrawling(false);
            }
        }, CRAWLER_POLLING_INTERVAL);
    };

    // On page load, check logs to see if a crawl is already in progress
    // (e.g. if the user refreshed mid-crawl). If the most recent "info" log
    // has no completion log after it, re-attach polling and disable the button.
    const checkCrawlerAlreadyRunning = async () => {
        try {
            const logs    = await getSavedTaskLogs(userData.token, userData.selectedOrganization.org_token, taskId);
            const logList = Array.isArray(logs?.data) ? logs.data : [];

            const sorted       = [...logList].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            const lastStartIdx = sorted.map((l) => l.type).lastIndexOf("info");

            if (lastStartIdx === -1) return; // no crawl has ever been started

            const logsAfterStart  = sorted.slice(lastStartIdx + 1);
            const alreadyFinished = logsAfterStart.some((l) => l.type === "success" || l.type === "error");

            if (!alreadyFinished) {
                setCrawling(true);
                startCrawlerPolling();
            }
        } catch {
            // Non-fatal — button will simply remain enabled
        }
    };

    // Clean up the interval if the component unmounts mid-crawl
    useEffect(() => {
        return () => stopPolling();
    }, []);

    useEffect(() => { getTealiumDataLayerReportData(); }, []);

    const getTealiumDataLayerReportData = async () => {
        try {
            setLoading(true);
            const result = await getTealiumDataLayerReportByTaskId(
                userData.token,
                userData.selectedOrganization.org_token,
                taskId
            );
            const rows = result?.data || [];
            setData(rows);

            // Seed validationMap from the most-recent stored validation in each record's latest capture
            const initialMap = {};
            rows.forEach((row) => {
                const capture = getLatestCapture(row);
                if (!capture?.validations?.length) return;

                const latest = capture.validations.reduce((a, b) =>
                    new Date(a.date_validated) >= new Date(b.date_validated) ? a : b
                );

                initialMap[row.full_url] = {
                    domain:  row.full_url,
                    fromDb:  true,
                    validation: {
                        is_valid:              latest.validation_status,
                        date_validated:        latest.date_validated,
                        total_rules_evaluated: (latest.rules_passed?.length ?? 0) + (latest.rules_failed?.length ?? 0),
                        passed_count:          latest.rules_passed?.length ?? 0,
                        violation_count:       latest.rules_failed?.length ?? 0,
                        passed: (latest.rules_passed ?? []).map((p) => ({
                            variable_name: p.variable_name,
                            rule_type:     p.rule_type,
                            scope:         p.scope,
                        })),
                        violations: (latest.rules_failed ?? []).map((v) => ({
                            variable_name: v.variable_name,
                            rule_type:     v.rule_type,
                            scope:         v.scope,
                            reason:        `Rule failed on last validation (${new Date(latest.date_validated).toLocaleString()}).`,
                        })),
                    },
                };
            });
            setValidationMap(initialMap);

            // Check if a crawl is already running (persists across page refreshes)
            await checkCrawlerAlreadyRunning();

            // Changes are now loaded on-demand via the "View Changes" button.
        } catch (error) {
            console.log("error===========> ", error);
            setError(error.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    // ── Fetch Changes Map ─────────────────────────────────────────────────────
    // Calls the backend for diffs on all entries matching the task's domain,
    // then keys the result by `${full_url}__${event_type}` so that the same URL
    // with different event types get independent entries in the table.

    const fetchValidationChanges = useCallback(async (date_a, date_b) => {
        try {
            const response = await getTealiumDataLayerValidationChanges(
                userData.token, userData.selectedOrganization.org_token, taskId,
                date_a, date_b
            );
            const changesList = Array.isArray(response?.data) ? response.data
                : Array.isArray(response) ? response : [];

            const map = {};
            changesList.forEach((item) => {
                const key = `${item.full_url}__${item.event_type}`;
                map[key] = item;
            });
            setChangesMap(map);
        } catch {
            // Non-fatal — changes column will show "No data"
        }
    }, [userData, taskId]);

    // ── Handle "View Changes" modal submission ────────────────────────────────
    // Called when the user picks two crawl dates and clicks Compare.

    const handleViewChangesSubmit = useCallback(async (date_a, date_b) => {
        setCrawlHistoryModal(false);
        setLoadingChanges(true);
        try {
            await fetchValidationChanges(date_a, date_b);
            toast.success("Changes loaded — scroll right to view the Changes column.");
        } catch {
            toast.error("Failed to load changes between selected dates.");
        } finally {
            setLoadingChanges(false);
        }
    }, [fetchValidationChanges]);

    // ── Run Crawler ───────────────────────────────────────────────────────────

    const handleRunCrawler = async () => {
        try {
            setCrawling(true);
            await runTealiumDataLayerCrawler(
                userData.token, userData.selectedOrganization.org_token, taskId
            );
            // The server responds immediately (202) — start polling logs
            // to detect when the background job finishes.
            startCrawlerPolling();
        } catch (err) {
            toast.error(err?.message || "Crawler failed.");
            setCrawling(false);
        }
    };

    // ── Validate All ──────────────────────────────────────────────────────────
    // Backend now derives page_urls from the task's domain — just pass task_id.

    const handleValidateAll = async () => {
        if (!data.length) return;
        try {
            setValidating(true);

            const response = await validateTealiumDataLayer(
                userData.token, userData.selectedOrganization.org_token, taskId
            );

            const completedValidations = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response)
                    ? response
                    : [];

            const newMap = { ...validationMap };
            const results = completedValidations.map((cv) => {
                const result = { domain: cv.page_url, validation: cv };
                newMap[cv.page_url] = result;
                return result;
            });

            setValidationMap(newMap);
            setValidationResults(results);

            // Refresh changes after a fresh validation so diffs reflect new data
            await fetchValidationChanges();
        } catch (err) {
            toast.error(err?.message || "Validation failed.");
        } finally {
            setValidating(false);
        }
    };

    // ── Download Changes Report ───────────────────────────────────────────────
    // Generates a CSV from the current changesMap — one row per changed attribute
    // per (full_url, event_type) pair. No backend call needed.

    const handleDownloadChangesReport = useCallback(() => {
        const rows = [];

        for (const changeData of Object.values(changesMap)) {
            // Skip entries that have no comparison data (e.g. only one capture exists)
            if (!changeData || changeData.message) continue;

            const {
                full_url,
                event_type,
                capture_dates,
                all_attribute_changes = {},
                rule_scoped_changes   = {},
            } = changeData;

            const earlier_capture_date = capture_dates?.previous ?? "";
            const later_capture_date   = capture_dates?.latest   ?? "";

            // All changed attributes across both rule-scoped and global changes
            const allPaths = new Set([
                ...Object.keys(rule_scoped_changes),
                ...Object.keys(all_attribute_changes),
            ]);

            for (const attribute_path of allPaths) {
                const isRuleScoped = Object.prototype.hasOwnProperty.call(rule_scoped_changes, attribute_path);
                const { from, to } = all_attribute_changes[attribute_path]
                    ?? rule_scoped_changes[attribute_path]
                    ?? {};

                const serialise = (v) => {
                    if (v === null || v === undefined) return "";
                    return typeof v === "object" ? JSON.stringify(v) : String(v);
                };

                rows.push({
                    full_url,
                    event_type:            event_type ?? "",
                    earlier_capture_date,
                    later_capture_date,
                    rule_scope_change:     isRuleScoped ? "True" : "False",
                    attribute_path,
                    earlier_value:         serialise(from),
                    later_value:           serialise(to),
                });
            }
        }

        if (!rows.length) {
            toast.info("No changes to export.");
            return;
        }

        const headers = [
            "full_url",
            "event_type",
            "earlier_capture_date",
            "later_capture_date",
            "rule_scope_change",
            "attribute_path",
            "earlier_value",
            "later_value",
        ];

        // RFC 4180: wrap any field that contains a comma, double-quote, or newline
        const escapeField = (val) => {
            const str = String(val ?? "");
            return str.includes(",") || str.includes('"') || str.includes("\n")
                ? `"${str.replace(/"/g, '""')}"`
                : str;
        };

        const csvLines = [
            headers.join(","),
            ...rows.map((r) => headers.map((h) => escapeField(r[h])).join(",")),
        ];

        const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href     = url;
        a.download = `changes-report-${taskId}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [changesMap, taskId]);
    // No deduplication — each DB document (unique full_url + event_type combo)
    // becomes its own row. The latest data_capture fields are surfaced at the top level.

    const flattenedData = useMemo(() => {
        if (!data?.length) return [];
        return data.map(row => {
            const flatRow     = { ...row };
            const capture     = getLatestCapture(row);
            const tealiumData = capture?.tealium_integration || {};
            const paramValueMap = {};

            (capture?.custom_parameters || []).forEach(p => {
                paramValueMap[p.key] = { value: p.value || "", custom_mapping_key: p.custom_mapping_key || "" };
            });

            const visibleParams = (row.saved_task_custom_parameters || []).filter(p => p.visibility);
            visibleParams.forEach(param => {
                const lookupKey = (param.custom_mapping_enabled && param.custom_mapping_key) ? param.custom_mapping_key : param.key;
                let value = paramValueMap[param.key]?.value || "";
                if (!value && tealiumData.hasOwnProperty(lookupKey)) value = String(tealiumData[lookupKey] ?? "");
                flatRow[`custom_param__${param.key}`] = value;
            });

            flatRow._latestCapture = capture;

            return flatRow;
        });
    }, [data]);

    const visibleCustomParamKeys = useMemo(() => {
        if (!data?.length) return [];
        const keySet = new Set();
        data.forEach(row => {
            (row.saved_task_custom_parameters || []).filter(p => p.visibility).forEach(p => keySet.add(p.key));
        });
        return Array.from(keySet);
    }, [data]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleViewImage = (rowData) => {
        const capture = getLatestCapture(rowData);
        setImageModal({ open: true, url: capture?.snapshot_img_url || "", domain: rowData.full_url });
    };

    const handleViewDetails = (rowData) => {
        const capture       = getLatestCapture(rowData);
        const visibleKeys   = (rowData.saved_task_custom_parameters || []).filter(p => p.visibility).map(p => p.key);
        const visibleParams = (capture?.custom_parameters || []).filter(p => visibleKeys.includes(p.key)).map(p => ({ ...p }));
        setEditedCustomParams(visibleParams);
        setActiveTab("tealium_integration");
        setDetailsModal({ open: true, rowData });
    };

    const handleCloseDetailsModal = () => {
        setDetailsModal({ open: false, rowData: null });
        setEditedCustomParams([]);
        setActiveTab("tealium_integration");
    };

    const handleCustomParamChange = (index, value) =>
        setEditedCustomParams((prev) => { const u = [...prev]; u[index] = { ...u[index], value }; return u; });

    const handleSaveCustomParams = async () => {
        try {
            setSaving(true);
            const row            = detailsModal.rowData;
            const captureIdx     = getLatestCaptureIdx(row);
            const capture        = row.data_captures[captureIdx];
            const existingParams = capture?.custom_parameters || [];

            const allParams = existingParams.map(p => {
                const edited = editedCustomParams.find(ep => ep._id === p._id || ep.key === p.key);
                return edited ? { ...p, value: edited.value } : p;
            });

            const patchPayload = {
                [`data_captures.${captureIdx}.custom_parameters`]: allParams
            };

            const result = await updateTealiumDataLayer(
                userData.token, userData.selectedOrganization.org_token, row._id, patchPayload
            );

            if (result?.status) {
                setData((prev) => prev.map((item) => {
                    if (item._id !== row._id) return item;
                    const updatedCaptures = item.data_captures.map((c, i) =>
                        i === captureIdx ? { ...c, custom_parameters: allParams } : c
                    );
                    return { ...item, data_captures: updatedCaptures };
                }));
                toast.success(result?.message || "Custom parameters updated successfully.");
                handleCloseDetailsModal();
            }
        } catch (error) {
            toast.error(error?.message || "Failed to update custom parameters.");
        } finally {
            setSaving(false);
        }
    };

    const handleCellValueChanged = useCallback((event) => {
        const { data: rowData, colDef, newValue, oldValue } = event;
        const field = colDef.field;
        if (!field?.startsWith("custom_param__") || newValue === oldValue) return;
        const paramKey = field.replace("custom_param__", "");
        setModifiedRows((prev) => ({ ...prev, [rowData._id]: { ...(prev[rowData._id] || {}), [paramKey]: newValue || "" } }));
    }, []);

    const handleSaveAllChanges = async () => {
        try {
            setSaving(true);
            for (const rowId of Object.keys(modifiedRows)) {
                const row = data.find(r => r._id === rowId);
                if (!row) continue;

                const changes    = modifiedRows[rowId];
                const captureIdx = getLatestCaptureIdx(row);
                const capture    = row.data_captures[captureIdx];

                const existingParams = capture?.custom_parameters || [];
                const existingKeys   = new Set(existingParams.map(p => p.key));
                const updatedParams  = existingParams.map(p => changes.hasOwnProperty(p.key) ? { ...p, value: changes[p.key] } : p);
                Object.keys(changes).forEach(key => { if (!existingKeys.has(key)) updatedParams.push({ key, value: changes[key] }); });

                const patchPayload = {
                    [`data_captures.${captureIdx}.custom_parameters`]: updatedParams
                };

                const result = await updateTealiumDataLayer(
                    userData.token, userData.selectedOrganization.org_token, rowId, patchPayload
                );
                if (result?.status) {
                    setData((prev) => prev.map((item) => {
                        if (item._id !== rowId) return item;
                        const updatedCaptures = item.data_captures.map((c, i) =>
                            i === captureIdx ? { ...c, custom_parameters: updatedParams } : c
                        );
                        return { ...item, data_captures: updatedCaptures };
                    }));
                }
            }
            setModifiedRows({});
            toast.success("All changes saved successfully.");
        } catch (error) {
            toast.error(error?.message || "Failed to save changes.");
        } finally {
            setSaving(false);
        }
    };

    const handleDiscardChanges = () => {
        setModifiedRows({});
        if (gridApi) {
            const freshData = data.map(row => {
                const flatRow = { ...row };
                const capture = getLatestCapture(row);
                (capture?.custom_parameters || []).forEach(param => {
                    flatRow[`custom_param__${param.key}`] = param.value || "";
                });
                return flatRow;
            });
            gridApi.setRowData(freshData);
        }
    };

    // ── Columns ───────────────────────────────────────────────────────────────

    const columns = useMemo(() => {
        const staticColumns = [
            {
                headerName: "URL",
                field: "full_url",
                sortable: true, filter: true, resizable: true, minWidth: 150,
                cellRenderer: (params) => (
                    <a href={params.data.full_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                        {params.data.full_url}
                    </a>
                ),
            },
            {
                headerName: "Status",
                field: "status",
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 120,
                cellRenderer: (params) => {
                    const status = params.data.status || "success";
                    const isSuccess = status === "success";
                    return (
                        <span
                            style={{
                                display: "inline-block",
                                padding: "2px 10px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: isSuccess ? "#15803d" : "#b91c1c",
                                backgroundColor: isSuccess ? "#dcfce7" : "#fee2e2",
                            }}
                        >
                            {isSuccess ? "Success" : "Failed"}
                        </span>
                    );
                },
            },
            {
                headerName: "Snapshot",
                field: "full_url",
                colId: "snapshot",
                sortable: false, filter: false, resizable: true, minWidth: 120,
                cellRenderer: (params) => (
                    <p className="text-primary cursor-pointer" onClick={() => handleViewImage(params.data)}>view image</p>
                ),
            },
            {
                headerName: "Report",
                field: "full_url",
                colId: "report",
                sortable: false, filter: false, resizable: true, minWidth: 120,
                cellRenderer: (params) => (
                    <p className="text-primary cursor-pointer" onClick={() => handleViewDetails(params.data)}>view details</p>
                ),
            },
            {
                headerName: "Captures",
                field: "data_captures",
                sortable: false, filter: false, resizable: true, minWidth: 100,
                cellRenderer: (params) => {
                    const count = params.data.data_captures?.length ?? 0;
                    return <span className="text-sm text-gray-600">{count}</span>;
                },
            },
            // ── Validation Status ──
            {
                headerName: "Validation Status",
                field: "full_url",
                colId: "validation_status",
                sortable: false, filter: false, resizable: true, minWidth: 160,
                cellRenderer: (params) => {
                    const entry = validationMap[params.data.full_url];
                    let status = "unvalidated";
                    if (entry) status = entry.error ? "failed" : entry.validation?.is_valid ? "passed" : "failed";
                    return <ValidationStatusBadge status={status} />;
                },
            },
            // ── Validation Report ──
            {
                headerName: "Validation Report",
                field: "full_url",
                colId: "validation_report",
                sortable: false, filter: false, resizable: true, minWidth: 160,
                cellRenderer: (params) => {
                    const entry = validationMap[params.data.full_url];
                    if (!entry) {
                        return <span className="text-xs text-gray-400 italic">Run validation to view</span>;
                    }
                    const violations = entry.validation?.violation_count ?? 0;
                    return (
                        <button
                            className="flex items-center gap-1.5 text-primary hover:opacity-75 text-xs font-medium"
                            onClick={() => setRowReportModal({ domain: params.data.full_url, result: entry })}
                        >
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {entry.error
                                ? "View error"
                                : entry.validation?.is_valid
                                    ? "All passed"
                                    : `${violations} violation${violations !== 1 ? "s" : ""}`}
                        </button>
                    );
                },
            },
            // ── Changes (rule-scoped) ──
            {
                headerName: "Changes",
                colId: "changes",
                sortable: false, filter: false, resizable: true, minWidth: 170,
                cellRenderer: (params) => {
                    const key        = `${params.data.full_url}__${params.data.event_type}`;
                    const changeData = changesMap[key];

                    // Not yet loaded or no prior capture to compare against
                    if (!changeData || changeData.message) {
                        return <ChangesBadge hasChanges={null} />;
                    }

                    const hasChanges = Object.keys(changeData.rule_scoped_changes ?? {}).length > 0;

                    return (
                        <button
                            className="flex items-center gap-2 hover:opacity-75 transition-opacity"
                            onClick={() => setChangesDiffModal({ fullUrl: params.data.full_url, changeData })}
                        >
                            <ChangesBadge hasChanges={hasChanges} />
                        </button>
                    );
                },
            },
        ];



        const endColumns = [
            {
                headerName: "Last Captured",
                field: "data_captures",
                colId: "last_captured",
                sortable: false, filter: false, resizable: true, minWidth: 180,
                cellRenderer: (params) => {
                    const capture = getLatestCapture(params.data);
                    return capture?.date_captured ? formatDate(capture.date_captured) : "—";
                },
            },
            {
                headerName: "Created At",
                field: "createdAt",
                sortable: true, filter: true, resizable: true, minWidth: 180,
                cellRenderer: (params) => formatDate(params.data?.createdAt),
            },
        ];

        return [...staticColumns, ...endColumns];
    }, [visibleCustomParamKeys, validationMap, changesMap]);

    const handleSearch = (e) => { setSearchText(e.target.value); gridApi.setQuickFilter(e.target.value); };
    const onGridReady  = (params) => { params.api.sizeColumnsToFit(); setGridApi(params.api); };

    const tabs = [
        { key: "tealium_integration", label: "Tealium Integration" },
        { key: "adobe_analytics",     label: "Adobe Analytics"     },
        { key: "custom_parameters",   label: "Custom Parameters"   },
    ];

    // ── Render ────────────────────────────────────────────────────────────────

    const detailsTealiumIntegration = detailsModal.rowData
        ? getLatestCapture(detailsModal.rowData)?.tealium_integration
        : null;

    const detailsAdobeAnalytics = detailsModal.rowData
        ? (getLatestCapture(detailsModal.rowData)?.adobe_analytics || [])
        : [];

    return (
        <div className="w-full max-w-screen px-4 md:px-8 my-6">
            <div className="w-full pb-2">
                <a href="#" className="font-medium text-primary hover:underline flex items-center"
                    onClick={(e) => { e.preventDefault(); navigate("/functions/saved-tasks"); }}>
                    <img src={backwardArrow} alt="" className="w-5 h-5 mr-2" />
                    {content?.tasks?.back_cta_text2}
                </a>
            </div>

            <main id="main" className="flex flex-col space-y-3 items-start flex-grow">
                <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                    Tealium Data Layer Report
                </h1>

                {/* Page-level tab navigation */}
                <div className="flex border-b w-full">
                    {[
                        { key: "site_crawl",  label: "Site Crawl"  },
                        { key: "path_crawls", label: "Path Crawls" },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setPageTab(tab.key)}
                            className={`py-2.5 px-6 text-sm font-medium transition-colors ${
                                pageTab === tab.key
                                    ? "border-b-2 border-primary text-primary"
                                    : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {pageTab === "path_crawls" && (
                    <PathCrawlsTab
                        taskId={taskId}
                        token={userData.token}
                        organizationId={userData.selectedOrganization.org_token}
                    />
                )}

                {pageTab === "site_crawl" && (
                    <>
                        <div className="flex flex-col pb-2 w-full">
                            <p>Tealium Data Layer Report Description</p>
                        </div>

                        <AgTableCard
                            className="w-full"
                            header={
                                <div className="flex justify-between w-full items-center flex-wrap gap-4">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="w-full sm:w-80">
                                            <Input
                                                value={searchText}
                                                onChange={handleSearch}
                                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                                placeholder="Search by URL"
                                                clearable
                                            />
                                        </div>
                                        <button
                                            onClick={handleRunCrawler}
                                            disabled={crawling}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                                        >
                                            {crawling ? (
                                                <>
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                    </svg>
                                                    Crawling...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                    Run Crawler
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={handleValidateAll}
                                            disabled={validating || !data.length}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                                        >
                                            {validating ? (
                                                <>
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                    </svg>
                                                    Validating...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                    </svg>
                                                    Validate All
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setCrawlHistoryModal(true)}
                                            disabled={loadingChanges || crawling}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                                        >
                                            {loadingChanges ? (
                                                <>
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                    </svg>
                                                    Loading…
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                    View Changes
                                                </>
                                            )}
                                        </button>
                                        {Object.values(changesMap).some((c) => c && !c.message) && (
                                            <button
                                                onClick={handleDownloadChangesReport}
                                                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-opacity"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Download Changes Report
                                            </button>
                                        )}
                                    </div>

                                    {hasUnsavedChanges && (
                                        <div className="flex items-center gap-3">
                                            <button onClick={handleDiscardChanges}
                                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50">
                                                Discard
                                            </button>
                                            <button onClick={handleSaveAllChanges} disabled={saving}
                                                className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50">
                                                {saving ? "Saving..." : "Save Changes"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            }
                            tableId="list-tealium-data-layer-report-table"
                            loading={loading}
                            emptyMessage="No Data Found"
                            rowData={flattenedData || []}
                            columnDefs={columns}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                            autoSizeStrategy={{ type: "fitGridWidth" }}
                            onGridReady={onGridReady}
                            onCellValueChanged={handleCellValueChanged}
                            stopEditingWhenCellsLoseFocus={true}
                        >
                            {error ? <h3 className="text-center py-6 text-red-500">{error}</h3> : null}
                        </AgTableCard>
                    </>
                )}
            </main>

            {/* Image Preview Modal */}
            {imageModal.open && (
                <PrimaryModal title={`Snapshot - ${imageModal.domain}`}
                    hideModal={() => setImageModal({ open: false, url: "", domain: "" })} widthClass="max-w-4xl">
                    <div className="p-4 flex justify-center items-center">
                        <img src={imageModal.url} alt={`Snapshot of ${imageModal.domain}`}
                            className="max-w-full max-h-[70vh] object-contain rounded" />
                    </div>
                </PrimaryModal>
            )}

            {/* Tealium / Custom Params Details Modal */}
            {detailsModal.open && detailsModal.rowData && (
                <PrimaryModal title={`Report - ${detailsModal.rowData.full_url}`}
                    hideModal={handleCloseDetailsModal} widthClass="max-w-3xl">
                    <div className="flex border-b">
                        {tabs.map((tab) => (
                            <button key={tab.key}
                                className={`flex-1 py-2 px-4 text-sm font-medium ${
                                    activeTab === tab.key ? "border-b-2 border-primary text-primary" : "text-gray-500 hover:text-gray-700"
                                }`}
                                onClick={() => setActiveTab(tab.key)}>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className="p-4 max-h-[70vh] overflow-y-auto">
                        {activeTab === "tealium_integration" && (
                            <table className="w-full text-sm text-left text-gray-700 border border-gray-200" style={{ tableLayout: "fixed" }}>
                                <thead className="text-xs text-gray-600 uppercase bg-gray-100">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold" style={{ width: "30%" }}>Key</th>
                                        <th className="px-4 py-3 font-semibold" style={{ width: "70%" }}>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailsTealiumIntegration
                                        ? Object.entries(detailsTealiumIntegration).map(([key, value], index) => (
                                            <tr key={`tealium-${key}`} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                <td className="px-4 py-3 font-medium text-gray-900 capitalize" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                    {key.replace(/_/g, " ")}
                                                </td>
                                                <td className="px-4 py-3" style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}>
                                                    {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                                </td>
                                            </tr>
                                        ))
                                        : (
                                            <tr><td className="px-4 py-3 text-gray-500 italic text-center" colSpan={2}>No tealium integration data available</td></tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        )}

                        {activeTab === "adobe_analytics" && (
                            detailsAdobeAnalytics.length === 0 ? (
                                <p className="text-gray-500 italic text-center py-4">No Adobe Analytics events captured for this page.</p>
                            ) : (
                                <div className="flex flex-col gap-4">
                                    {detailsAdobeAnalytics.map((aaEvent, eventIndex) => (
                                        <div key={`aa-event-${eventIndex}`} className="border border-gray-200 rounded">
                                            <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                                                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                    {aaEvent.event || "AA Event"}
                                                </span>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    {aaEvent.random != null && (
                                                        <span>Random: <span className="font-mono">{aaEvent.random}</span></span>
                                                    )}
                                                    {aaEvent.timestamp && (
                                                        <span>{new Date(aaEvent.timestamp).toLocaleTimeString()}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <table className="w-full text-sm text-left text-gray-700" style={{ tableLayout: "fixed" }}>
                                                <thead className="text-xs text-gray-600 uppercase bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 font-semibold" style={{ width: "30%" }}>Key</th>
                                                        <th className="px-4 py-2 font-semibold" style={{ width: "70%" }}>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {aaEvent.data && Object.entries(aaEvent.data).map(([key, value], rowIndex) => (
                                                        <tr key={`aa-${eventIndex}-${key}`} className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                            <td className="px-4 py-2 font-medium text-gray-900" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                                {key}
                                                            </td>
                                                            <td className="px-4 py-2" style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}>
                                                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            )
                        )}

                        {activeTab === "custom_parameters" && (
                            <>
                                {editedCustomParams.length === 0 ? (
                                    <p className="text-gray-500 italic text-center py-4">No visible custom parameters available.</p>
                                ) : (
                                    <>
                                        <table className="w-full text-sm text-left text-gray-700 border border-gray-200" style={{ tableLayout: "fixed" }}>
                                            <thead className="text-xs text-gray-600 uppercase bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-3 font-semibold" style={{ width: "30%" }}>Key</th>
                                                    <th className="px-4 py-3 font-semibold" style={{ width: "70%" }}>Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editedCustomParams.map((param, index) => (
                                                    <tr key={`custom-${param._id || index}`} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                        <td className="px-4 py-3 font-medium text-gray-900 capitalize" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                            {param.key?.replace(/_/g, " ")}
                                                        </td>
                                                        <td className="px-4 py-3" style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}>
                                                            <input type="text" value={param.value || ""}
                                                                onChange={(e) => handleCustomParamChange(index, e.target.value)}
                                                                className="w-full p-2 text-sm text-gray-900 bg-white rounded border border-gray-300 focus:outline-none focus:border-primary" />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="flex justify-end mt-4">
                                            <button onClick={handleSaveCustomParams} disabled={saving}
                                                className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50">
                                                {saving ? "Saving..." : "Save"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </PrimaryModal>
            )}

            {/* Per-row Validation Report Modal */}
            {rowReportModal && (
                <RowValidationModal
                    domain={rowReportModal.domain}
                    result={rowReportModal.result}
                    onClose={() => setRowReportModal(null)}
                />
            )}

            {/* Bulk Validation Summary Modal */}
            {validationResults && (
                <ValidationResultsModal
                    results={validationResults}
                    onClose={() => setValidationResults(null)}
                />
            )}

            {/* Changes Diff Modal */}
            {changesDiffModal && (
                <ChangesDiffModal
                    fullUrl={changesDiffModal.fullUrl}
                    changeData={changesDiffModal.changeData}
                    onClose={() => setChangesDiffModal(null)}
                />
            )}

            {/* Crawl History / View Changes Modal */}
            {crawlHistoryModal && (
                <CrawlHistoryModal
                    taskId={taskId}
                    token={userData.token}
                    organizationId={userData.selectedOrganization.org_token}
                    onSubmit={handleViewChangesSubmit}
                    onClose={() => setCrawlHistoryModal(false)}
                />
            )}

        </div>
    );
};

export default TealiumDataLayerReport;